import { useFetch } from "@hyper-fetch/react";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";

import { client } from "@/api/client";
import { sdk } from "@/api/sdk";
import { BOOKING_CACHE_PATTERN } from "@/features/booking/hooks/use-booking-change";
import type { ClientMembership, ClientQuery } from "@/features/booking/types/client.types";

/**
 * Remembers which business a person with several of them looked at last. It stores their client
 * id there, not the business id: a client id belongs to one person, so the next person signing
 * in on the same browser never inherits the choice.
 */
export const BUSINESS_STORAGE_KEY = "booking.client.selected";

const listeners = new Set<() => void>();

/** Storage is a convenience: when it is blocked the first business is shown. */
export const readStoredClientId = (): string | null => {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(BUSINESS_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const writeStoredClientId = (clientId: string) => {
  try {
    window.localStorage.setItem(BUSINESS_STORAGE_KEY, clientId);
  } catch {
    // Blocked storage: the choice lasts for this page only.
  }
  for (const listener of listeners) listener();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  window.addEventListener("storage", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
};

/** The business on screen: the remembered one while it is still theirs, else the first. */
export const pickMembership = (memberships: ClientMembership[], storedClientId: string | null) =>
  memberships.find((candidate) => candidate.clientId === storedClientId) ?? memberships[0] ?? null;

/** Sent on every client call, so a person with two businesses gets the right one. */
export const clientQueryOf = (membership: ClientMembership | null): ClientQuery =>
  membership ? { organizationId: membership.organizationId } : {};

const NO_MEMBERSHIPS: ClientMembership[] = [];

let openSignUp: Promise<ClientMembership | null> | null = null;

/**
 * Open sign-up at a business with a public booking page: no code, the account becomes a client.
 * Null when there is no such business, which is the normal answer for invite-only ones. Screens
 * mounted together share one request.
 *
 * `organizationId` is which business, and on a platform it matters: without it the backend joins
 * whichever business is public and oldest, so a client arriving from one trainer's page could end
 * up on another's books. It comes off the `org` the public page put in the booking link.
 */
export const joinPublicBusiness = (organizationId?: string): Promise<ClientMembership | null> => {
  openSignUp ??= sdk.bookingClient.joinPublic.$post
    .send({ payload: organizationId ? { organizationId } : {} })
    .then(({ data }) => {
      openSignUp = null;
      return data ?? null;
    });
  return openSignUp;
};

/**
 * The business a person was sent here from, as the public page wrote it into the URL. Read from
 * the live location rather than a route's typed search, because every client screen needs it and
 * only one of them has `org` in its own schema.
 */
export const arrivedFromBusiness = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  return new URLSearchParams(window.location.search).get("org") ?? undefined;
};

/**
 * Who is booking and with which business. Most people have exactly one business; the switcher
 * only shows with more.
 *
 * A page opened for one trainer (`org` in the URL, from a card on the Book page or a public
 * page's booking link) is about that trainer. Somebody who is not their client yet is signed up
 * there, once, before anything renders, so the specialist's page and its times work the same as
 * for their own trainer's team. Without an `org` nobody is signed up anywhere: the pack joined
 * "the public business" then, which on a platform is whichever trainer registered first - a
 * stranger's client list for somebody who had not chosen anyone. `linked` is false only for
 * somebody with no business at all, and every client screen shows the platform's trainers then.
 */
export function useBookingClient() {
  const me = useFetch(sdk.bookingClient.me.$get);
  const stored = useSyncExternalStore(subscribe, readStoredClientId, () => null);
  const [from] = useState(arrivedFromBusiness);
  const needsJoin = Boolean(from && me.data && !me.data.some((candidate) => candidate.organizationId === from));
  const signUpStarted = useRef(false);
  const [signUp, setSignUp] = useState<{ settled: boolean; membership: ClientMembership | null }>({
    settled: false,
    membership: null,
  });

  useEffect(() => {
    if (!needsJoin || !from || signUpStarted.current) return;
    signUpStarted.current = true;
    const signUpOnce = async () => {
      const membership = await joinPublicBusiness(from);
      setSignUp({ settled: true, membership });
      // Every read so far was answered without this business: ask again.
      if (membership) client.cache.invalidate(BOOKING_CACHE_PATTERN);
    };
    void signUpOnce();
  }, [needsJoin, from]);

  // The new membership stands in until the list catches up, so nothing flashes in between.
  const memberships = useMemo(() => {
    const listed = me.data ?? NO_MEMBERSHIPS;
    const joined = signUp.membership;
    if (!joined || listed.some((candidate) => candidate.organizationId === joined.organizationId)) return listed;
    return [...listed, joined];
  }, [me.data, signUp.membership]);
  const membership =
    memberships.find((candidate) => candidate.organizationId === from) ?? pickMembership(memberships, stored);
  const organizationId = membership?.organizationId ?? null;

  const query = useMemo<ClientQuery>(() => (organizationId ? { organizationId } : {}), [organizationId]);

  const selectBusiness = useCallback(
    (nextOrganizationId: string) => {
      const next = memberships.find((candidate) => candidate.organizationId === nextOrganizationId);
      if (next) writeStoredClientId(next.clientId);
    },
    [memberships],
  );

  return {
    /** Only the very first load, open sign-up included: a refetch keeps the screen up. */
    loading: (me.loading && !me.data) || (needsJoin && !signUp.settled),
    error: me.data ? null : me.error,
    refetch: me.refetch,
    memberships,
    membership,
    linked: membership !== null,
    organizationId,
    query,
    /** The team paused new bookings for this person. Booked visits stay and can still be changed. */
    blocked: membership?.blocked === true,
    selectBusiness,
  };
}

export type BookingClient = ReturnType<typeof useBookingClient>;
